// mosaicCell — the canvas reconstruction for one Mosaic grid cell (DOM-only).
//
// Ports web/mosaic.js's `makeClip` / `makeImageCell`: a cell knows how to draw
// itself into the grid canvas at output time `t`. A Clip keeps its compact frames
// (palette indices + palette) plus ONE reconstruction canvas it reuses across
// output frames — rebuilt only when the shown source frame changes (real-speed
// looping, cheap). A photo is a one-frame static cell, drawn the same every frame.
//
// This is the thin, DOM-dependent wrapper over the pure timeline in mosaic.ts
// (frameForTime). Not unit-tested (needs a canvas) — the load-bearing frame
// SELECTION is tested in mosaic.test.ts; this only paints the chosen frame.

import type { GifFrame } from "./gifDecode";
import { clipDuration, frameForTime } from "./mosaic";

/** A grid cell: its loop duration (0 for a photo) + a paint-at-time-`t` method. */
export interface MosaicCell {
  /** Loop duration in seconds (0 = a static photo cell). */
  readonly duration: number;
  /** Draw this cell into `ctx` at (`x`,`y`), `size`×`size`, for output time `t`s. */
  drawCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    t: number,
  ): void;
}

/**
 * An animated Clip cell from decoded GIF frames. Reconstructs the shown frame
 * into a reused offscreen canvas (only when the source-frame index changes) and
 * blits it scaled into the grid — web/mosaic.js's `makeClip`, reusing the pure
 * `frameForTime` timeline.
 */
export function makeClipCell(frames: readonly GifFrame[]): MosaicCell {
  const first = frames[0]!;
  const w = first.width;
  const h = first.height;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2d canvas context unavailable");
  const img = ctx.createImageData(w, h);
  let lastFrame = -1;

  return {
    duration: clipDuration(frames),
    drawCell(g, x, y, size, t) {
      const j = frameForTime(frames, t);
      if (j !== lastFrame) {
        const frame = frames[j]!;
        const { indices, palette } = frame;
        const data = img.data;
        for (let k = 0; k < indices.length; k++) {
          const c = indices[k]! * 3;
          const o = k * 4;
          data[o] = palette[c]!;
          data[o + 1] = palette[c + 1]!;
          data[o + 2] = palette[c + 2]!;
          data[o + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
        lastFrame = j;
      }
      g.drawImage(canvas, x, y, size, size);
    },
  };
}

/** A still photo as a one-frame cell — drawn identically for every output frame. */
export function makeImageCell(image: CanvasImageSource): MosaicCell {
  return {
    duration: 0,
    drawCell(g, x, y, size) {
      g.drawImage(image, x, y, size, size);
    },
  };
}
